import { useState, useContext, useEffect, memo } from "react";
import {
  Modal,
  TextField,
  Button,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  Box,
} from "@material-ui/core";
import { useStyles } from "../Styles/styles";
import { UserContext } from "../Contexts/UserContext";
import { useMutation } from "@apollo/client";
import { SetupContext } from "../Contexts/SetupContext";
import { SET_STATUS } from "../Mutations/SetStatus";
import HelpIconButton from "./HelpIconButton";
import { DashboardContext } from "../Contexts/DashboardContext";
import { AVAILABLE_OFFICER_ACTIONS } from "../Actions/AvailableOfficerActions";
import moment from "moment";

const RadioLogModal = ({
  modalOpen,
  setModalOpen,
  incident,
  incidents,
  officer,
}) => {
  const classes = useStyles();
  const { user, nightMode } = useContext(UserContext);
  const { radioLogCodeId, checkForSetup } = useContext(SetupContext);
  const { availableOfficerDispatch } = useContext(DashboardContext);
  const { UPDATE_AVAILABLE_OFFICER } = AVAILABLE_OFFICER_ACTIONS;
  const [localIncident, setLocalIncident] = useState(incident);
  const [entryText, setEntryText] = useState("");

  const localOisId = localIncident?.officerIncidentSpecifics.find(
    ois => ois?.officer?.id.toString() === user?.id.toString()
  )?.id;
  const myIncidents =
    incidents?.length > 0
      ? incidents?.filter(i =>
          i.officerIncidentSpecifics.some(
            el => el?.officer?.id.toString() === user?.id.toString()
          )
        )
      : [];

  const handleClose = () => {
    setEntryText("");
    setModalOpen(false);
  };

  const [setStatus] = useMutation(SET_STATUS);

  const addRadioLogEntry = () => {
    let oisId = officer?.oisId || null;
    const localDate = moment().utc().format("YYYY-MM-DD HH:mm:ss") + " UTC";
    if (localIncident) {
      oisId = localIncident.officerIncidentSpecifics.find(
        ois => ois?.officer?.id.toString() === user?.id.toString()
      )?.id;
    }
    if (oisId) {
      setStatus({
        variables: {
          input: {
            originatorId: user?.id,
            updateCallLogOnly: true,
            logCodeId: radioLogCodeId,
            officerIncidentSpecificId: oisId,
            entryText: entryText,
            officerId: officer?.id || user?.id,
          },
        },
      });
      availableOfficerDispatch({
        type: UPDATE_AVAILABLE_OFFICER,
        payload: {
          officerId: user?.id,
          property: "lastContactedAt",
          value: localDate,
        },
      });
    } else {
      setStatus({
        variables: {
          input: {
            originatorId: user?.id,
            updateCallLogOnly: true,
            logCodeId: radioLogCodeId,
            entryText: entryText,
            officerId: officer?.id || user?.id,
          },
        },
      });
      availableOfficerDispatch({
        type: UPDATE_AVAILABLE_OFFICER,
        payload: {
          officerId: user?.id,
          property: "lastContactedAt",
          value: localDate,
        },
      });
    }
    handleClose();
  };

  useEffect(() => {
    if (modalOpen && !radioLogCodeId) {
      checkForSetup?.(false, true);
    }
  }, [modalOpen, radioLogCodeId, checkForSetup]);

  return (
    <Modal
      open={modalOpen}
      onClose={() => setModalOpen(false)}
      className={classes.modal}
      data-cy="address-history-modal"
      keepMounted={false}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          width: "50vw",
          overflow: "auto",
        }}
        className={classes.dmvModals}
      >
        <div
          id="header"
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: nightMode ? "#91918f" : "#fae8ad",
            borderRadius: "5px",
            paddingRight: "1vw",
          }}
        >
          <h3
            style={{ color: nightMode ? "white" : "black", marginLeft: "1vw" }}
          >
            {!localOisId && "Global "}Radio Log Entry:{" "}
            {localOisId
              ? localIncident?.incidentNumber
              : `${
                  myIncidents?.find(i => i?.id === localIncident?.id)
                    ?.incidentNumber || ""
                }`}
          </h3>
          <HelpIconButton objectname="mobile_radio_log_modal" />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignContent: "space-between",
            overflowY: "auto",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-end",
              justifyContent: "flex-start",
            }}
          >
            {!incident?.id && myIncidents?.length > 0 && (
              <FormControl className={classes.matchMargin}>
                <InputLabel
                  shrink={true}
                  className={classes.label}
                  data-cy={"incident-select-label"}
                  id={"incident-select-label"}
                >
                  Incident
                </InputLabel>
                <Select
                  value={
                    myIncidents?.find(i => i?.id === localIncident?.id) || ""
                  }
                  onChange={e => setLocalIncident(e.target.value)}
                  renderValue={value => value?.incidentNumber}
                  autoWidth={true}
                >
                  <MenuItem
                    key="blank-incident-for-global-log-entry"
                    value={null}
                  ></MenuItem>
                  {myIncidents &&
                    myIncidents.map(i => (
                      <MenuItem key={i?.id} value={i}>
                        {i?.incidentNumber}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            )}
            {officer && (
              <TextField
                data-cy="officer-badge"
                inputProps={{ "data-cy": "officer-badge-text-input" }}
                id="officer-badge-text-id"
                label="Badge"
                value={officer?.badge}
                style={{ width: "fit-content" }}
                className={classes.matchMargin}
                disabled={true}
              />
            )}
            {/* eslint-disable-next-line jsx-a11y/no-autofocus -- Focus the log entry when this modal opens. */}
            <TextField autoFocus
              multiline
              data-cy="radio-log-text"
              inputProps={{ "data-cy": "radio-log-text-input" }}
              id="radio-log-text-id"
              label="Log Entry"
              value={entryText}
              style={{ width: "100%" }}
              className={classes.matchMargin}
              onChange={e => setEntryText(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addRadioLogEntry();
                }
              }}
            />
          </div>
          <Box display="flex" flexDirection="row" justifyContent="flex-end">
            <Button
              style={{
                width: "fit-content",
                marginTop: "1vh",
                marginBottom: "3vh",
                marginRight: "2vw",
              }}
              variant="contained"
              onClick={addRadioLogEntry}
              disabled={!radioLogCodeId}
            >
              Add
            </Button>
            <Button
              style={{
                width: "fit-content",
                marginTop: "1vh",
                marginBottom: "3vh",
                marginRight: "2vw",
              }}
              variant="contained"
              onClick={handleClose}
            >
              Close
            </Button>
          </Box>
          {!radioLogCodeId && (
            <p
              style={{
                marginLeft: "1vw",
                color: nightMode ? "white" : "black",
              }}
            >
              Waiting for radio log code...
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
};
export default memo(RadioLogModal);
